import { useState, useEffect } from 'react';
import {X, GraduationCap, Building2, Loader2, ArrowRight, CheckCircle2} from 'lucide-react';
import toast from 'react-hot-toast';
import { REGIONS, type Region } from '@/data/formations';
import { useFormDraft } from '@/hooks/useFormDraft';

// Clé de brouillon local pour ne jamais perdre les données saisies.
const FORMATION_FORM_DRAFT_KEY = 'bnssa-mns:draft:formation-form';

export type FormationCategory = 'BNSSA' | 'MNS' | 'Recyclage' | 'Perfectionnement';

/** Coordonnées de l'organisme collectées dans la pop-up. */
export interface FormationCompanyData {
  raisonSociale: string;
  siret?: string;
  adresse?: string;
  telephone: string;
  email?: string;
  responsableNom: string;
  responsableFonction?: string;
}

/** Données de la formation collectées dans la pop-up. */
export interface FormationData {
  title: string;
  category: FormationCategory;
  region: Region;
  location: string;
  sessionDate: string;
  duration?: string;
  description?: string;
  programme?: string;
  prerequisites?: string;
  certification?: string;
  contact?: string;
}

/** Payload complet émis par la pop-up. */
export interface FormationDepositPayload {
  company: FormationCompanyData;
  formation: FormationData;
}

interface Props {
  /** Email pré-rempli si disponible (organisme connecté). */
  email?: string;
  /** Coordonnées pré-remplies si l'organisme connecté possède déjà un profil. */
  initialCompany?: Partial<FormationCompanyData>;
  /** Masquer le bloc coordonnées si l'organisme a déjà des coordonnées complètes. */
  hideCompanyBlock?: boolean;
  /**
   * Brouillon de repli (brouillon de pré-inscription déjà soumis) pour
   * réafficher coordonnées + formation si l'utilisateur revient sur la pop-up
   * après être allé sur /inscription sans terminer la création de compte.
   */
  fallbackDraft?: FormationDepositPayload | null;
  onClose: () => void;
  onSave: (data: FormationDepositPayload) => void | Promise<void>;
  submitLabel?: string;
  saving?: boolean;
}

/**
 * FormationFormModal — Pop-up de dépôt de formation.
 *
 * Reprend les champs « Mes coordonnées » (organisme) + « Nouvelle formation »,
 * sans dépendance directe à un compte : la pop-up collecte les données et les
 * transmet via `onSave`. Même principe que AvailabilityFormModal / JobFormModal.
 */
export default function FormationFormModal({
  email,
  initialCompany,
  hideCompanyBlock = false,
  fallbackDraft,
  onClose,
  onSave,
  submitLabel = 'Continuer',
  saving = false,
}: Props) {
  // Brouillon local : restauré au montage, sauvegardé à chaque frappe.
  const { initialDraft, saveDraft, clearDraft } = useFormDraft<Record<string, string>>(
    FORMATION_FORM_DRAFT_KEY
  );
  // Lecture initiale du brouillon (typée souple) pour pré-remplir les champs.
  const d = (initialDraft || {}) as Record<string, string | undefined>;
  // Repli sur le brouillon de pré-inscription (structure { company, formation }).
  const fbC = fallbackDraft?.company;
  const fbF = fallbackDraft?.formation;

  // ── Coordonnées organisme ────────────────────────────────────────
  const [raisonSociale, setRaisonSociale] = useState(initialCompany?.raisonSociale || d.raisonSociale || fbC?.raisonSociale || '');
  const [siret, setSiret] = useState(initialCompany?.siret || d.siret || fbC?.siret || '');
  const [adresse, setAdresse] = useState(initialCompany?.adresse || d.adresse || fbC?.adresse || '');
  const [telephone, setTelephone] = useState(initialCompany?.telephone || d.telephone || fbC?.telephone || '');
  const [companyEmail, setCompanyEmail] = useState(initialCompany?.email || email || d.companyEmail || fbC?.email || '');
  const [responsableNom, setResponsableNom] = useState(initialCompany?.responsableNom || d.responsableNom || fbC?.responsableNom || '');
  const [responsableFonction, setResponsableFonction] = useState(
    initialCompany?.responsableFonction || d.responsableFonction || fbC?.responsableFonction || ''
  );

  // ── Formation ────────────────────────────────────────────────────
  const [title, setTitle] = useState(d.title || fbF?.title || '');
  const [category, setCategory] = useState<FormationCategory>((d.category as FormationCategory) || fbF?.category || 'BNSSA');
  const [region, setRegion] = useState<Region>((d.region as Region) || (fbF?.region as Region) || ('Île-de-France' as Region));
  const [location, setLocation] = useState(d.location || fbF?.location || '');
  const [sessionDate, setSessionDate] = useState(d.sessionDate || (fbF?.sessionDate ? fbF.sessionDate.slice(0, 10) : ''));
  const [duration, setDuration] = useState(d.duration || fbF?.duration || '');
  const [description, setDescription] = useState(d.description || fbF?.description || '');
  const [programme, setProgramme] = useState(d.programme || fbF?.programme || '');
  const [prerequisites, setPrerequisites] = useState(d.prerequisites || fbF?.prerequisites || '');
  const [certification, setCertification] = useState(d.certification || fbF?.certification || '');
  const [contact, setContact] = useState(d.contact || fbF?.contact || (email ? email : ''));

  // Sauvegarde automatique du brouillon à chaque modification d'un champ.
  useEffect(() => {
    saveDraft({
      raisonSociale,
      siret,
      adresse,
      telephone,
      companyEmail,
      responsableNom,
      responsableFonction,
      title,
      category,
      region: region as string,
      location,
      sessionDate,
      duration,
      description,
      programme,
      prerequisites,
      certification,
      contact,
    });
  }, [
    saveDraft,
    raisonSociale,
    siret,
    adresse,
    telephone,
    companyEmail,
    responsableNom,
    responsableFonction,
    title,
    category,
    region,
    location,
    sessionDate,
    duration,
    description,
    programme,
    prerequisites,
    certification,
    contact,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation coordonnées (sauf si bloc masqué car déjà complètes)
    if (!hideCompanyBlock) {
      if (!raisonSociale.trim() || !telephone.trim() || !responsableNom.trim()) {
        toast.error('Merci de renseigner le nom du centre, le téléphone et le nom complet du responsable.');
        return;
      }
      if (companyEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail.trim())) {
        toast.error("L'adresse email renseignée n'est pas valide.");
        return;
      }
      const cleanSiret = siret.replace(/\s+/g, '');
      if (cleanSiret && !/^\d{14}$/.test(cleanSiret)) {
        toast.error('Si renseigné, le numéro SIRET doit contenir exactement 14 chiffres.');
        return;
      }
    }

    // Validation formation
    if (!title.trim() || !location.trim() || !sessionDate) {
      toast.error('Merci de renseigner le titre, le lieu et la date de la session.');
      return;
    }

    await onSave({
      company: {
        raisonSociale: raisonSociale.trim(),
        siret: siret.replace(/\s+/g, '') || undefined,
        adresse: adresse.trim() || undefined,
        telephone: telephone.trim(),
        email: companyEmail.trim() || undefined,
        responsableNom: responsableNom.trim(),
        responsableFonction: responsableFonction.trim() || undefined,
      },
      formation: {
        title: title.trim(),
        category,
        region,
        location: location.trim(),
        sessionDate,
        duration: duration.trim() || undefined,
        description: description.trim() || undefined,
        programme: programme.trim() || undefined,
        prerequisites: prerequisites.trim() || undefined,
        certification: certification.trim() || undefined,
        contact: contact.trim() || undefined,
      },
    });
    // Envoi réussi → on nettoie le brouillon local.
    clearDraft();
  };

  const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-white w-full sm:max-w-2xl max-h-[92vh] sm:rounded-2xl rounded-t-2xl overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 bg-gradient-to-r from-amber-50 via-white to-red-50 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900">Annoncer une formation</h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Renseignez vos coordonnées d'organisme et votre session. Votre formation sera{' '}
              <strong>publiée après validation</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-6 overflow-y-auto">
          {/* ── Bloc coordonnées ──────────────────────────────────── */}
          {!hideCompanyBlock && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                  <Building2 className="w-4 h-4" />
                </span>
                <h3 className="text-sm font-bold text-gray-900">Mes coordonnées</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Ces informations sont visibles uniquement par notre administration et ne sont jamais
                affichées sur les pages publiques.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Nom du centre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={raisonSociale}
                    onChange={(e) => setRaisonSociale(e.target.value)}
                    placeholder="Ex : Centre Aquatique du Lac"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Numéro SIRET <span className="font-normal text-gray-500">(optionnel)</span>
                  </label>
                  <input
                    type="text"
                    value={siret}
                    onChange={(e) => setSiret(e.target.value)}
                    placeholder="14 chiffres"
                    maxLength={20}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Téléphone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                    placeholder="Ex : 04 78 12 34 56"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Email <span className="font-normal text-gray-500">(optionnel)</span>
                  </label>
                  <input
                    type="email"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    placeholder="Ex : contact@centre.fr"
                    className={inputCls}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Adresse complète <span className="font-normal text-gray-500">(optionnel)</span>
                  </label>
                  <input
                    type="text"
                    value={adresse}
                    onChange={(e) => setAdresse(e.target.value)}
                    placeholder="Ex : 12 rue des Sources, 69003 Lyon"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Nom complet <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={responsableNom}
                    onChange={(e) => setResponsableNom(e.target.value)}
                    placeholder="Ex : Marie Dupont"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Fonction <span className="font-normal text-gray-500">(optionnel)</span>
                  </label>
                  <input
                    type="text"
                    value={responsableFonction}
                    onChange={(e) => setResponsableFonction(e.target.value)}
                    placeholder="Ex : Directrice"
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Bloc formation ────────────────────────────────────── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-700 flex items-center justify-center">
                <GraduationCap className="w-4 h-4" />
              </span>
              <h3 className="text-sm font-bold text-gray-900">Nouvelle formation</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Titre de la formation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex : Formation BNSSA — Session printemps"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Catégorie <span className="text-red-500">*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as FormationCategory)}
                  className={`${inputCls} bg-white`}
                >
                  <option value="BNSSA">BNSSA</option>
                  <option value="MNS">MNS</option>
                  <option value="Recyclage">Recyclage</option>
                  <option value="Perfectionnement">Perfectionnement</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Région <span className="text-red-500">*</span>
                </label>
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value as Region)}
                  className={`${inputCls} bg-white`}
                >
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Ville / Lieu (ville + département) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Ex : Lyon (69)"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Date de la session <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Durée</label>
                <input
                  type="text"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="Ex : 6 semaines / 180 heures"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Description courte
                </label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Présentation générale de la formation, objectifs, public visé…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Programme détaillé
                </label>
                <textarea
                  rows={4}
                  value={programme}
                  onChange={(e) => setProgramme(e.target.value)}
                  placeholder={`Une ligne par module ou journée. Ex :
Jour 1 — Sauvetage aquatique & techniques de remorquage
Jour 2 — Réglementation, surveillance et POSS`}
                  className={`${inputCls} resize-none`}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Astuce : une ligne par point pour un affichage en liste sur la fiche publique.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Prérequis</label>
                <textarea
                  rows={3}
                  value={prerequisites}
                  onChange={(e) => setPrerequisites(e.target.value)}
                  placeholder="Ex : 18 ans, PSE1 à jour, certificat médical…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Certification délivrée
                </label>
                <input
                  type="text"
                  value={certification}
                  onChange={(e) => setCertification(e.target.value)}
                  placeholder="Ex : Diplôme BNSSA, Attestation PSE1…"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Contact pour inscription (téléphone, email ou site)
                </label>
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="Ex : 04 78 12 34 56 — contact@centre.fr"
                  className={inputCls}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {submitLabel}
            {!saving && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
